import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Truck } from "lucide-react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackAnalytics } from "@/lib/analytics";
import { serviceOptions } from "@/lib/pricing";
import { cn } from "@/lib/utils";
import { postcodeSchema, serviceSchema } from "@/lib/validation";
import type { ServiceType } from "@/types/booking";
import type { StepProps } from "@/components/booking/steps/types";
import { StepFrame } from "@/components/booking/steps/StepFrame";
import { wasteKingAssets } from "@/lib/siteAssets";

const servicePostcodeSchema = postcodeSchema.merge(serviceSchema);

type ServiceForm = z.infer<typeof servicePostcodeSchema>;
type ServiceFormInput = z.input<typeof servicePostcodeSchema>;

export function ServiceStep({ state, isLocked, updateState, goNext }: StepProps) {
  const form = useForm<ServiceFormInput, unknown, ServiceForm>({
    resolver: zodResolver(servicePostcodeSchema),
    defaultValues: {
      postcode: state.postcode,
      service: state.service || undefined,
    },
  });

  function selectService(service: ServiceType) {
    form.setValue("service", service, { shouldDirty: true, shouldValidate: true });
    updateState({
      service,
      skipSize: service === "skip" ? state.skipSize : "",
      loadSize: service === "man-van" ? state.loadSize : "",
      materialType: service === "grab" ? state.materialType : "",
    });
    trackAnalytics("service selected", { service });

    if (service === "skip") {
      void form.handleSubmit(onSubmit)();
    }
  }

  function onSubmit(values: ServiceForm) {
    const postcode = values.postcode.trim().toUpperCase();
    updateState({
      postcode,
      service: values.service,
      bookingDetails: { ...state.bookingDetails, postcode },
    });
    trackAnalytics("service step completed", { postcode, service: values.service });
    goNext();
  }

  const selectedService = form.watch("service");

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <StepFrame title="Choose a service" description="Enter the job postcode and choose the Waste King service you need." isReadOnly={isLocked}>
        <div className="mb-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="max-w-md space-y-2">
            <Label htmlFor="postcode">Postcode</Label>
            <Input
              id="postcode"
              placeholder="Enter your postcode"
              autoComplete="postal-code"
              {...form.register("postcode")}
              readOnly={isLocked}
              disabled={isLocked}
            />
            {form.formState.errors.postcode ? <p className="text-sm font-semibold text-destructive">{form.formState.errors.postcode.message}</p> : null}
          </div>
          <div className="hidden overflow-hidden rounded-lg border-2 border-accent/10 bg-accent lg:block">
            <img src={wasteKingAssets.brandGuide.cover} alt="Waste King branded fleet" className="h-32 w-full object-cover object-center" />
          </div>
        </div>
        <input type="hidden" {...form.register("service")} />
        <div className="grid gap-3 md:grid-cols-3 md:gap-4">
          {serviceOptions.map((service) => {
            const isSelected = selectedService === service.id;
            const isLockedAlternative = isLocked && !isSelected;

            return (
              <Card
                key={service.id}
                className={cn(
                  "overflow-hidden rounded-lg transition-all",
                  isSelected && "border-primary bg-primary/5",
                  isLockedAlternative && "border-muted bg-muted/20 shadow-none",
                )}
              >
                <CardContent className="flex h-full p-0 sm:p-0 md:flex-col">
                  <div className={cn("flex w-20 shrink-0 items-center justify-center bg-secondary md:aspect-video md:w-full", isLockedAlternative && "bg-muted/50")}>
                    <Truck className={cn("h-8 w-8 text-accent md:h-10 md:w-10", isLockedAlternative && "text-muted-foreground/45")} />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 md:gap-4 md:p-5">
                    <div className="space-y-1 md:space-y-2">
                      <h3 className={cn("text-base font-black md:text-lg", isLockedAlternative && "text-muted-foreground/60")}>{service.name}</h3>
                      <p className={cn("line-clamp-2 text-sm leading-5 text-muted-foreground md:line-clamp-none md:leading-6", isLockedAlternative && "text-muted-foreground/45")}>{service.description}</p>
                    </div>
                    <Button
                      type="button"
                      className={cn(
                        "mt-auto w-full md:w-auto",
                        isSelected && isLocked && "disabled:opacity-100",
                        isLockedAlternative && "border-muted bg-white/40 text-muted-foreground/50 shadow-none disabled:opacity-100",
                      )}
                      variant={isSelected ? "default" : "outline"}
                      onClick={() => selectService(service.id)}
                      disabled={isLocked}
                    >
                      {isSelected ? "Selected" : "Select"}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {form.formState.errors.service ? <p className="mt-3 text-sm font-semibold text-destructive">{form.formState.errors.service.message}</p> : null}
      </StepFrame>
    </form>
  );
}
